import React, { useEffect, useRef, useState } from "react";
import {
  useGlobalTalentSearch,
  useGlobalTalentSearchQuery,
  useGlobalTalentSearchResumeDownload,
  useFetchCitiesByCountry,
} from "../../../store/query/globalTalentSearchQuery";
import { useFormik } from "formik";
import { useToast } from "../../../contexts-hooks/hooks/useToster";
import { ExternalApiEndPoint } from "../../../api-routes";

const STORAGE_KEY = "global_talent_search_state";

const loadSavedState = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {
    console.error("Error loading search state from sessionStorage:", e);
  }
  return null;
};

const useGlobalSearch = () => {
  const [savedState] = useState(() => loadSavedState());
  const [data, setData] = useState(savedState?.data || []);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const [selectedValues, setSelectedValues] = useState({
    job_title: "",
    location: [],
    skills: [],
    experience: "",
  });

  const [query, setQuery] = useState({ query: "" });

  const [disable, setDisabled] = useState(true);
  const [backDrop, setBackDrop] = useState(false);

  const [filterOptions, setFilterOptions] = useState(
    savedState?.filterOptions || { page: 1, limit: 10 }
  );

  const [isGlobalSearching, setIsGlobalSearching] = useState(false);

  const [expandedSkills, setExpandedSkills] = useState({});
  const [expandedCards, setExpandedCards] = useState({});
  const [cardHeights, setCardHeights] = useState({});
  const [locationInputs, setLocationInputs] = useState(
    savedState?.locationInputs || []
  );
  const [skillInput, setSkillInput] = useState(savedState?.skillInput || "");
  const [downloadingResume, setDownloadingResume] = useState({});

  const [isGlobalSearchingQuery, setIsGlobalSearchingQuery] = useState(false);

  const [accordionExpanded, setAccordionExpanded] = useState(false);

  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [cities, setCities] = useState([]);
  const [citySearchQuery, setCitySearchQuery] = useState("");
  const [loadingCities, setLoadingCities] = useState(false);

  const countryOptions = [
    { name: "United States", code: "US", flag: `${ExternalApiEndPoint.FLAG_CDN}/us.png` },
    { name: "India", code: "IN", flag: `${ExternalApiEndPoint.FLAG_CDN}/in.png` },
  ];

  const { showToast } = useToast();

  const searchSuggestions = [
    "Python",
    "Python Developer",
    "AI Engineer with 3 years experience",
    "DevOps Engineer with 3 years experience",
    "Machine Learning Engineer with 4+ years experience",
    "Senior Data Scientist with 7+ years experience",
    "DevOps",
    "React",
    "Salesforce developer",
    "Data Scientist",
    "Lead Data Scientist",
    "Business Analyst",
    "Data Engineer",
    "Java Developer with 3 years experience",
    "Senior React Developer",
    "Frontend Developer with 4 years experience",
    "Backend Engineer",
    "Full Stack Developer with 5 years experience",
    "Node.js Developer",
    "UI/UX Designer",
    "MERN Stack Developer with 4 years experience",
  ];

  const experienceMarks = [
    { value: 0, label: "0" },
    { value: 3, label: "3+" },
    { value: 6, label: "6+" },
    { value: 9, label: "9+" },
    { value: 12, label: "12+" },
    { value: 15, label: "15+" },
  ];

  const headCells = [
    { numeric: true, disablePadding: true, label: "Candidate Name" },
    { numeric: true, disablePadding: true, label: "Candidate Email" },
    { numeric: true, disablePadding: true, label: "Phone Number" },
    { numeric: true, disablePadding: false, label: "Education" },
    { numeric: false, disablePadding: true, label: "Job Title" },
    { numeric: false, disablePadding: true, label: "Experience" },
    { numeric: false, disablePadding: true, label: "Year of Experience" },
    { numeric: true, disablePadding: false, label: "Skills" },
    { numeric: false, disablePadding: true, label: "Certifications" },
    { numeric: true, disablePadding: false, label: "Location" },
    { numeric: true, disablePadding: false, label: "Similarity Score" },
    { numeric: true, disablePadding: false, label: "Download" },
  ];

  const handleSuggestionClick = (suggestion) => {
    setQuery({ ...query, query: suggestion });
    setAccordionExpanded(false);
  };
  const handlePrevPage = () => {
    setFilterOptions((prev) => ({
      ...prev,
      page: Math.max(1, prev.page - 1),
    }));
  };

  const handleNextPage = () => {
    console.log("Next button clicked");
    setExpandedCards({});
    setExpandedSkills({});
    setCardHeights({});
    const maxPage = Math.ceil(data.results?.length / filterOptions.limit);
    setFilterOptions((prev) => ({
      ...prev,
      page: Math.min(maxPage, prev.page + 1),
    }));
  };
  const paginatedData = data.results
    ? data.results.slice(
        (filterOptions.page - 1) * filterOptions.limit,
        filterOptions.page * filterOptions.limit
      )
    : [];
  const globalTalentSearchMutation = useGlobalTalentSearch();
  const fetchCitiesMutation = useFetchCitiesByCountry();
  const handleSearchGlobalTalent = (payload) => {
    setIsGlobalSearching(true);
    globalTalentSearchMutation.mutate(payload, {
      onSuccess: (response) => {
        if (response) {
          setData(response);
          setIsGlobalSearching(false);
        }
      },
    });
  };
  const { mutate: setGlobalTalent } = useGlobalTalentSearchQuery();
  const handleSearchGlobalTalentQuery = async () => {
    setIsGlobalSearchingQuery(true);
    const payload = { query: query.query?.toString() || "" };
    setGlobalTalent(payload, {
      onSuccess: (response) => {
        if (response) {
          setData(response);
          setIsGlobalSearchingQuery(false);
        }
      },
      onError: (error) => {
        console.error("API Error:", error);
        setIsGlobalSearchingQuery(false);
      },
    });
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const { mutate: downloadResume } = useGlobalTalentSearchResumeDownload();

  const handleDownloadResume = (resumeId, resumeName, buttonKey) => {
    if (!resumeId) {
      setSnackbar({
        open: true,
        message: "Resume is not available for this candidate.",
        severity: "warning",
      });
      return;
    }

    const key = buttonKey || resumeId;
    setDownloadingResume((prev) => ({ ...prev, [key]: true }));

    downloadResume(resumeId, {
      onSuccess: async (response) => {
        setDownloadingResume((prev) => ({ ...prev, [key]: false }));

        if (!response || response.size === 0) {
          setSnackbar({
            open: true,
            message: "Resume file is not available for download.",
            severity: "error",
          });
          return;
        }

        const type = response.type || "";
        if (type.includes("json")) {
          try {
            const text = await response.text();
            const json = JSON.parse(text);
            setSnackbar({
              open: true,
              message: json.message || "Resume file is not available for download.",
              severity: "error",
            });
          } catch {
            setSnackbar({
              open: true,
              message: "Resume file is not available for download.",
              severity: "error",
            });
          }
          return;
        }

        let extension = ".pdf";
        if (type.includes("word") || type.includes("msword")) {
          extension = ".doc";
        } else if (type.includes("officedocument.wordprocessingml.document")) {
          extension = ".docx";
        }

        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `resume_${resumeName || "candidate"}_${resumeId}${extension}`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      onError: async (error) => {
        setDownloadingResume((prev) => ({ ...prev, [key]: false }));

        let errorMessage = "Resume is not available for this candidate.";

        if (error?.response?.data instanceof Blob && error.response.data.type?.includes("json")) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            if (json.message) errorMessage = json.message;
          } catch {
          }
        } else if (error?.response?.status === 404) {
          errorMessage = "Resume is not available for this candidate.";
        } else if (error?.message && !error.message.includes("Network Error")) {
          errorMessage = error.message;
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      },
    });
  };

  const handleChange = (event, id) => {
    const { value, checked } = event.target;
    setSelectedValues((prev) => ({
      ...prev,
      [id]: checked ? value : prev[id],
    }));
    setDisabled(false);
  };
  const handleRowsPerPageChange = (event) =>
    setFilterOptions((prev) => ({
      ...prev,
      limit: event.target.value,
    }));

  useEffect(() => {
    console.log("Filter options changed:", filterOptions);
    if (data.results && data.results.length > 0) {
      scrollRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [data]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  useEffect(() => {
    if (data.results && data.results.length === 0) {
      showToast(" No candidates found ", "error");
    }
  }, [data.results]);

  const formik = useFormik({
    initialValues: savedState?.formikValues || {
      job_title: "",
      location: [],
      skills: [],
      experience: [0, 15],
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      const [minExp, maxExp] = values.experience;
      const experienceStr =
        maxExp >= 15 ? `${minExp}-15+` : `${minExp}-${maxExp}`;
      const payload = {
        job_title: values.job_title,
        location: values.location,
        skills: values.skills,
        experience: experienceStr,
      };
      handleSearchGlobalTalent(payload);
    },
  });

  useEffect(() => {
    if (
      (data?.results && data.results.length > 0) ||
      locationInputs.length > 0 ||
      skillInput ||
      formik.values.job_title ||
      formik.values.skills.length > 0
    ) {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          data,
          locationInputs,
          skillInput,
          filterOptions,
          formikValues: formik.values,
        })
      );
    }
  }, [data, locationInputs, skillInput, filterOptions, formik.values]);

  const handleResetSearch = () => {
    setLocationInputs([]);
    setSkillInput("");
    formik.resetForm({
      values: {
        job_title: "",
        location: [],
        skills: [],
        experience: [0, 15],
      },
    });
  };

  const handleClearSearchResults = () => {
    setData([]);
    setQuery({ query: "" });
    setLocationInputs([]);
    setSkillInput("");
    setFilterOptions({ page: 1, limit: 10 });
    formik.resetForm({
      values: {
        job_title: "",
        location: [],
        skills: [],
        experience: [0, 15],
      },
    });
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isGlobalSearchingQuery && query.query?.trim()) {
      handleSearchGlobalTalentQuery();
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setLoadingCities(true);
    setCitySearchQuery("");
    setCities([]);
    fetchCitiesMutation.mutate(country.name, {
      onSuccess: (result) => {
        if (result && !result.error && result.data) {
          setCities(result.data);
        }
        setLoadingCities(false);
      },
      onError: (error) => {
        console.error("Failed to fetch cities:", error);
        setCities([]);
        setLoadingCities(false);
      },
    });
  };

  const handleCitySelect = (city) => {
    const code = selectedCountry?.code || "";
    const locationDisplay = `${code} - ${city}`;
    if (locationInputs.includes(locationDisplay)) {
      setLocationInputs((prev) => prev.filter((l) => l !== locationDisplay));
      formik.setFieldValue(
        "location",
        formik.values.location.filter((c) => c !== city)
      );
    } else {
      setLocationInputs((prev) => [...prev, locationDisplay]);
      formik.setFieldValue("location", [...formik.values.location, city]);
    }
  };

  const handleLocationRemove = (locationToRemove) => {
    setLocationInputs((prev) => prev.filter((l) => l !== locationToRemove));
    const dashIdx = locationToRemove.indexOf(" - ");
    const city = dashIdx !== -1 ? locationToRemove.substring(dashIdx + 3) : locationToRemove;
    formik.setFieldValue(
      "location",
      formik.values.location.filter((c) => c !== city)
    );
  };

  const handleLocationDropdownClose = () => {
    setLocationDropdownOpen(false);
    setSelectedCountry(null);
    setCities([]);
    setCitySearchQuery("");
  };

  const handleLocationClear = () => {
    setLocationInputs([]);
    formik.setFieldValue("location", []);
    setLocationDropdownOpen(false);
    setSelectedCountry(null);
    setCities([]);
    setCitySearchQuery("");
  };

  const filteredCities = cities.filter((city) =>
    city.toLowerCase().includes(citySearchQuery.toLowerCase())
  );

  const toggleSkillsExpansion = (candidateId) => {
    setExpandedSkills((prev) => ({
      ...prev,
      [candidateId]: !prev[candidateId],
    }));
  };

  const toggleCardExpansion = (candidateId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [candidateId]: !prev[candidateId],
    }));
  };

  const checkCardHeight = (candidateId, element) => {
    if (element) {
      const cardHeight = element.scrollHeight;
      const viewportHeight = window.innerHeight;
      const shouldCollapse = cardHeight > viewportHeight * 0.5;

      setCardHeights((prev) => ({
        ...prev,
        [candidateId]: { height: cardHeight, shouldCollapse },
      }));
    }
  };
  return {
    data,
    scrollRef,
    selectedValues,
    paginatedData,
    formik,
    headCells,
    backDrop,
    filterOptions,
    disable,
    query,
    isGlobalSearching,
    isGlobalSearchingQuery,
    inputRef,
    scrollRef,
    locationInputs,
    skillInput,

    setSelectedValues,
    setQuery,
    setDisabled,
    handlePrevPage,
    handleNextPage,
    handleChange,
    handleDownloadResume,
    handleSearchGlobalTalentQuery,
    handleSearchGlobalTalent,
    handleKeyPress,
    setSkillInput,
    toggleSkillsExpansion,
    toggleCardExpansion,
    checkCardHeight,
    setDownloadingResume,
    downloadingResume,
    snackbar,
    setSnackbar,

    accordionExpanded,
    setAccordionExpanded,
    searchSuggestions,
    handleSuggestionClick,

    expandedSkills,
    setExpandedSkills,
    expandedCards,
    setExpandedCards,
    cardHeights,
    setCardHeights,
    experienceMarks,

    locationDropdownOpen,
    setLocationDropdownOpen,
    selectedCountry,
    countryOptions,
    cities,
    citySearchQuery,
    setCitySearchQuery,
    loadingCities,
    filteredCities,
    handleCountrySelect,
    handleCitySelect,
    handleLocationDropdownClose,
    handleLocationRemove,
    handleLocationClear,
    handleResetSearch,
    handleClearSearchResults,
  };
};

export default useGlobalSearch;
